import { useQuery } from '@tanstack/react-query'; // Note that we are NOT using our hand-rolled one because this doesn't return Response.data
import type { QueryKey } from '@tanstack/react-query';

import { get } from '../../../redux/api';
import { nuc } from '../../../utils';
import type { LicenseSummary } from '../../../../shared/types/licenses';
import type { License } from '../../../types';

//  We can keep this data around for a bit longer since license data basically never changes.
const STALE_TIME = 1000 * 60 * 30; // 30 minutes

export type LicenseResponse = License & {
  Summary?: LicenseSummary;
};

/**
 * Async function to fetch data about a license., format it, and return it.
 *
 * @param {string} licenseId The ID of the license to fetch.
 * @returns {Promise<LicenseData>} The formatted license data.
 */
async function getLicense(licenseId: string) {
  const { data } = await get<LicenseResponse>(`/api/licenses/${nuc(licenseId)}`);
  if (!data) {
    throw new Error(`License "${licenseId}" could not be fetched!`);
  }
  return data;
}

const getQueryKey = (licenseId: string): QueryKey => ['license', licenseId];

/**
 * Hook which will fetch data about a given license.
 * Caches the license data for 10 minutes, since it's unlikely to ever change.
 *
 * @param {string} licenseId The ID of the license to fetch.
 * @param {boolean} shouldFetch Whether to fetch the license data or not. Defaults to true.
 */
export default function useLicense(licenseId: string, shouldFetch = true) {
  return useQuery<LicenseResponse>({
    queryKey: getQueryKey(licenseId),
    queryFn: () => getLicense(licenseId),
    staleTime: STALE_TIME,
    enabled: shouldFetch && !!licenseId,
  });
}
